import {
  FolderOpen,
  FileText,
  MoreHorizontal,
  Edit,
  Trash2,
  Copy,
  ChevronRight,
  ChevronDown,
} from 'lucide-react'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { cn } from '@/lib/utils'
import type { TemplateHubTree } from '@/lib/api/types/hubs'

// Available hub icons - Using LucideIcon type for proper typing
import type { LucideIcon } from 'lucide-react'

export const HUB_ICONS: Record<string, LucideIcon> = {
  folder: FolderOpen,
  file: FileText,
  // Add more icons as needed
}

// Available hub colors
export const HUB_COLORS = [
  { name: 'Blue', value: 'from-blue-400 to-blue-600', bg: 'bg-blue-500' },
  { name: 'Purple', value: 'from-violet-400 to-purple-600', bg: 'bg-violet-500' },
  { name: 'Green', value: 'from-green-400 to-green-600', bg: 'bg-green-500' },
  { name: 'Orange', value: 'from-orange-400 to-orange-600', bg: 'bg-orange-500' },
  { name: 'Pink', value: 'from-pink-400 to-pink-600', bg: 'bg-pink-500' },
  { name: 'Teal', value: 'from-teal-400 to-teal-600', bg: 'bg-teal-500' },
  { name: 'Red', value: 'from-red-400 to-red-600', bg: 'bg-red-500' },
  { name: 'Gray', value: 'from-gray-400 to-gray-600', bg: 'bg-gray-500' },
]

interface HubCardProps {
  hub: TemplateHubTree
  onSelect?: (hub: TemplateHubTree) => void
  onEdit?: (hub: TemplateHubTree) => void
  onDelete?: (hub: TemplateHubTree) => void
  onDuplicate?: (hub: TemplateHubTree) => void
  isExpanded?: boolean
  onToggleExpand?: () => void
  depth?: number
  showActions?: boolean
}

export function HubCard({
  hub,
  onSelect,
  onEdit,
  onDelete,
  onDuplicate,
  isExpanded = false,
  onToggleExpand,
  depth = 0,
  showActions = true,
}: HubCardProps) {
  const hasChildren = hub.children && hub.children.length > 0
  const hubIconKey = hub.icon ?? ''
  const IconComponent = HUB_ICONS[hubIconKey] ?? FolderOpen
  const colorClass = hub.color || 'from-blue-400 to-blue-600'

  return (
    <div className="group">
      <div
        className={cn(
          'flex items-center gap-3 p-3 rounded-xl border border-transparent',
          'hover:bg-gray-50 dark:hover:bg-gray-800/50 hover:border-gray-200 dark:hover:border-gray-700',
          'transition-all duration-200 cursor-pointer',
          !hub.isActive && 'opacity-60'
        )}
        style={{ paddingLeft: `${depth * 24 + 12}px` }}
        onClick={() => onSelect?.(hub)}
      >
        {/* Expand/Collapse Button */}
        {hasChildren && (
          <button
            onClick={(e) => {
              e.stopPropagation()
              onToggleExpand?.()
            }}
            className="p-1 rounded-md hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
          >
            {isExpanded ? (
              <ChevronDown className="h-4 w-4 text-gray-500" />
            ) : (
              <ChevronRight className="h-4 w-4 text-gray-500" />
            )}
          </button>
        )}
        {!hasChildren && <div className="w-6" />}

        {/* Icon */}
        <div className={cn(
          'p-2.5 rounded-xl bg-gradient-to-br shadow-sm',
          colorClass
        )}>
          <IconComponent className="h-4 w-4 text-white" />
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <h3 className="font-medium text-gray-900 dark:text-white truncate">
              {hub.name}
            </h3>
            {!hub.isActive && (
              <span className="inline-flex px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400">
                Inactive
              </span>
            )}
          </div>
          {hub.description && (
            <p className="text-sm text-gray-500 dark:text-gray-400 truncate mt-0.5">
              {hub.description}
            </p>
          )}
        </div>

        {/* Template Count Badge */}
        <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-gray-100 dark:bg-gray-800">
          <FileText className="h-3.5 w-3.5 text-gray-500" />
          <span className="text-xs font-medium text-gray-600 dark:text-gray-300">
            {hub.templateCount}
          </span>
        </div>

        {/* Actions Menu */}
        {showActions && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                onClick={(e) => e.stopPropagation()}
                className={cn(
                  'p-2 rounded-lg transition-colors',
                  'opacity-0 group-hover:opacity-100',
                  'hover:bg-gray-200 dark:hover:bg-gray-700'
                )}
              >
                <MoreHorizontal className="h-4 w-4 text-gray-500" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-48 rounded-xl">
              <DropdownMenuItem
                onClick={(e) => {
                  e.stopPropagation()
                  onEdit?.(hub)
                }}
                className="rounded-lg"
              >
                <Edit className="h-4 w-4 mr-2" />
                Edit hub
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={(e) => {
                  e.stopPropagation()
                  onDuplicate?.(hub)
                }}
                className="rounded-lg"
              >
                <Copy className="h-4 w-4 mr-2" />
                Duplicate
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onClick={(e) => {
                  e.stopPropagation()
                  onDelete?.(hub)
                }}
                className="text-red-600 dark:text-red-400 rounded-lg focus:bg-red-50 dark:focus:bg-red-900/20"
              >
                <Trash2 className="h-4 w-4 mr-2" />
                Delete
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>
    </div>
  )
}

interface HubTreeProps {
  hubs: TemplateHubTree[]
  expandedIds: Set<string>
  onToggleExpand: (id: string) => void
  onSelect?: (hub: TemplateHubTree) => void
  onEdit?: (hub: TemplateHubTree) => void
  onDelete?: (hub: TemplateHubTree) => void
  onDuplicate?: (hub: TemplateHubTree) => void
  depth?: number
  showActions?: boolean
}

export function HubTree({
  hubs,
  expandedIds,
  onToggleExpand,
  onSelect,
  onEdit,
  onDelete,
  onDuplicate,
  depth = 0,
  showActions = true,
}: HubTreeProps) {
  return (
    <div className="space-y-1">
      {hubs.map((hub) => {
        const isExpanded = expandedIds.has(hub.id)
        const hasChildren = hub.children && hub.children.length > 0

        return (
          <div key={hub.id}>
            <HubCard
              hub={hub}
              onSelect={onSelect}
              onEdit={onEdit}
              onDelete={onDelete}
              onDuplicate={onDuplicate}
              isExpanded={isExpanded}
              onToggleExpand={() => onToggleExpand(hub.id)}
              depth={depth}
              showActions={showActions}
            />
            {hasChildren && isExpanded && (
              <HubTree
                hubs={hub.children}
                expandedIds={expandedIds}
                onToggleExpand={onToggleExpand}
                onSelect={onSelect}
                onEdit={onEdit}
                onDelete={onDelete}
                onDuplicate={onDuplicate}
                depth={depth + 1}
                showActions={showActions}
              />
            )}
          </div>
        )
      })}
    </div>
  )
}
